import {
  compareMoney,
  getCurrency,
  MNT,
  money as makeMoney,
  type Money,
  pow10,
  sumMoney,
} from "@shoppy/accounting";
import type { Locale } from "@shoppy/i18n";
import type { MoneyDto } from "@shoppy/shared/money";
import { ABSENT } from "./format";

/**
 * Money — docs/24 §8a. **This is the only module in the console that turns an
 * amount into a string.** Not `Intl.NumberFormat` at a call site, and not a grid
 * `valueFormatter` written per column.
 *
 * Failure mode: a console that formats MNT its own way is a second
 * implementation of a rounding decision the ledger already made. The two agree
 * until a half-tögrög lands somewhere, and then the screen and the filed pack
 * disagree by one — which is the whole class of defect the money package's
 * property tests exist to prevent.
 *
 * ── The scale is asked for, never assumed ───────────────────────────────────
 * The figure is built from `@shoppy/accounting`'s `Money` — the monorepo's one
 * money type (CLAUDE.md, ADR-0012) — and the number of minor units in a major
 * unit comes from that package's currency table, through `getCurrency`. The
 * previous implementation delegated to `formatMnt` in `@shoppy/shared/money`,
 * which divides by a literal `100n`: correct for MNT, and silently 100× wrong
 * the first time a zero-decimal amount reaches a screen. The ledger already
 * carries JPY and KRW at scale 0 for exactly the AWS/app-store billing docs/22
 * §7 describes, so «MNT is the only currency» is true of the storefront and not
 * of this console's finance screens.
 *
 * Nothing here divides money, rounds it or scales it. `pow10` is the accounting
 * package's own memoised power of ten, and the split below is an exact integer
 * quotient and remainder of a `bigint` — the same arithmetic `decimalToString`
 * performs, done here only so the whole part can be grouped and the minor part
 * cannot be.
 *
 * What is NOT taken from `@shoppy/accounting` is `moneyToString`, which renders
 * `"15000.00 MNT"`. That is a debugging shape: ungrouped, always two decimals,
 * the code in the wrong place for either language. A display formatter belongs
 * where the locale is, and the locale is here.
 */

/** The grouping locale per UI language. Grouping only — the amount is exact before it arrives. */
const INTL_LOCALE: Record<Locale, string> = { mn: "mn-MN", en: "en-US" };

/**
 * The tögrög's mark. Every other currency renders with its ISO code instead:
 * inventing a symbol for a currency nobody has checked the placement of is how
 * a screen states an amount in the wrong money, and `USD 1,200` is never
 * ambiguous where `$1,200` on a Mongolian finance screen might be.
 */
const CURRENCY_MARKS: Record<string, string> = { MNT: "₮" };

/**
 * U+00A0, written as an escape rather than typed. A figure and the money it is
 * denominated in must never wrap apart, and a literal non-breaking space in
 * source is invisible to the next reader — indistinguishable from the ordinary
 * one that would let `1,200` and `USD` land on different lines.
 */
const NBSP = "\u00a0";

/**
 * The one formatter.
 *
 * MNT is displayed with no minor unit, grouped, with the mark before the figure.
 * A figure that shows `.00` invites somebody to type into it; the minor units
 * appear only when they are non-zero, which is where hiding them would misstate
 * the amount (a VAT line, an allocation).
 *
 * A negative is a real minus, and it precedes the mark: `-₮1,500`, never
 * `₮-1,500`. Not a colour (§2.4 — colour alone fails a contrast theme) and not
 * brackets, which are an accountancy convention this console's readers do not
 * all share and which sort badly in an export.
 *
 * A figure and its currency are ONE value. MNT is very nearly the only currency,
 * which is exactly why an unlabelled number is easy to ship and impossible to
 * check later.
 */
export function formatMoney(value: Money, locale: Locale): string {
  const { minorScale } = getCurrency(value.currency);
  const scale = pow10(minorScale);
  const negative = value.units < 0n;
  const abs = negative ? -value.units : value.units;
  const whole = abs / scale;
  const fraction = abs % scale;
  const grouped = new Intl.NumberFormat(INTL_LOCALE[locale], { useGrouping: true }).format(whole);
  const minor = fraction === 0n ? "" : `.${fraction.toString().padStart(minorScale, "0")}`;
  const mark = CURRENCY_MARKS[value.currency];
  const sign = negative ? "-" : "";
  return mark === undefined
    ? `${sign}${grouped}${minor}${NBSP}${value.currency}`
    : `${sign}${mark}${grouped}${minor}`;
}

/** The wire DTO — `{ currency, units }` with the units as a decimal string. */
export function asMoney(dto: MoneyDto | null | undefined, locale: Locale): string {
  const value = toMoney(dto?.units, dto?.currency);
  return value === null ? ABSENT : formatMoney(value, locale);
}

/**
 * The same, for the many DTOs that carry minor units as a bare decimal string
 * (`payableUnits`, `amountUnits`, `totalUnits`). The currency is not on the wire
 * there because those endpoints are MNT-only; it is added here so nothing
 * reaches a screen unlabelled.
 */
export function asUnits(units: string | null | undefined, locale: Locale): string {
  const value = toMoney(units, MNT);
  return value === null ? ABSENT : formatMoney(value, locale);
}

/**
 * Decode a wire amount, or `null`.
 *
 * `moneyFromJson` is the package's own decoder and it **raises** on a malformed
 * string or an unknown currency, which is right for a ledger posting and wrong
 * for a grid cell: one bad row off the wire would take out the whole register
 * rather than showing an em dash in one cell. So the two failure conditions are
 * checked here and turn into `null`, which every caller draws as ABSENT (§8 —
 * an em dash says «there is no figure», a blank says «this failed to load»).
 */
function toMoney(units: string | null | undefined, currency: string | undefined): Money | null {
  if (units === null || units === undefined || units === "" || currency === undefined) return null;
  if (!/^-?\d+$/.test(units)) return null;
  try {
    return makeMoney(BigInt(units), getCurrency(currency).code);
  } catch {
    return null;
  }
}

/**
 * Sorting a money column. AG Grid compares whatever the value getter returns,
 * and a decimal STRING compares lexicographically — "9" after "10000000" — so a
 * register sorted by amount would be wrong in a way that looks deliberate.
 *
 * `compareMoney` is the package's comparator and is used where both cells are
 * real amounts. It deliberately **raises** on a currency mismatch, and it takes
 * no nulls; a comparator that throws would break the grid's sort on the first
 * empty cell, so absence is ranked here — below every figure, so an unfilled row
 * never sorts above a real one — and the comparison itself is the ledger's.
 */
export function compareUnits(a: string | null | undefined, b: string | null | undefined): number {
  const left = toMoney(a, MNT);
  const right = toMoney(b, MNT);
  if (left === null && right === null) return 0;
  if (left === null) return -1;
  if (right === null) return 1;
  return compareMoney(left, right);
}

/**
 * A column total. `sumMoney` refuses a mixed-currency sum rather than adding the
 * numbers, which is the invariant worth having: the alternative is a caption
 * that states a total in a currency no line was denominated in. Rows that carry
 * no figure contribute nothing — an absent balance is not a zero balance, but it
 * is not a reason to refuse the total either.
 *
 * Returns `Money`, not `bigint`, so a caller cannot render the sum without going
 * back through {@link formatMoney}.
 */
export function sumUnits(values: readonly (string | null | undefined)[]): Money {
  const amounts: Money[] = [];
  for (const value of values) {
    const amount = toMoney(value, MNT);
    if (amount !== null) amounts.push(amount);
  }
  return sumMoney(amounts, MNT);
}

/**
 * A COD shift's shortfall is **signed**: positive is cash missing, negative is
 * cash that appeared. Drawn as a plain amount, an overage reads as a shortfall —
 * and an overage is as much a signal as a shortfall, because it means a
 * collection was not recorded against the unit it belongs to.
 *
 * So the sign is decoded here, into three named outcomes, and the screen draws a
 * WORD for each rather than leaving the reader to notice a minus (§2.4).
 */
export type Shortfall =
  | { readonly kind: "level"; readonly absUnits: string }
  | { readonly kind: "missing"; readonly absUnits: string }
  | { readonly kind: "over"; readonly absUnits: string };

export function shortfallOf(units: string | null | undefined): Shortfall | null {
  const value = toMoney(units, MNT);
  if (value === null) return null;
  if (value.units === 0n) return { kind: "level", absUnits: "0" };
  return value.units > 0n
    ? { kind: "missing", absUnits: value.units.toString() }
    : { kind: "over", absUnits: (-value.units).toString() };
}
